//LIBRARIES
import React, { Component, Fragment } from 'react';
import { reduxForm } from 'redux-form';
import moment from 'moment';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import FlatButton from 'material-ui/FlatButton';
import withMobileDialog from '@material-ui/core/withMobileDialog';

//HELPERS
import * as formatter from 'helpers/Formatter';
import { AccentColor } from 'helpers/AccentColorHelper';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';
import { hostIsBoardwalk, hostIsCapreit } from 'helpers/CurrentWindowLocation';

//ASSETS
import billIcon from 'assets/autopay_bill_icon.png';

//COMPONENTS
import validate from '../components/tenant_application/validate';
import {
  AUTOPAY_INFORMATION_STEP,
  AUTOPAY_DETAILS_STEP,
  AUTOPAY_PAYMENT_ASSIGNMENT_STEP,
  AUTOPAY_MODIFY_MONTHLY_AMOUNT,
  AUTOPAY_CONFIRMATION_STEP,
  AUTOPAY_SUBMIT_MONTHLY_AMOUNT,
} from 'components/tenant_app/components/payments/autopay/AutopaySettings';
import AutoPayDetails from 'components/tenant_app/components/payments/autopay/form/AutoPayDetails';
import AutoPayAmountDetails from 'components/tenant_app/components/payments/autopay/form/AutoPayAmountDetails';
import AutoPayActiveDetails from 'components/tenant_app/components/payments/autopay/form/AutoPayActiveDetails';
import AutoPayConfirmation from 'components/tenant_app/components/payments/autopay/form/AutoPayConfirmation';
import AutoPayExplaination from 'components/tenant_app/components/payments/autopay/form/AutoPayExplaination';

const style = {
  badge: {
    textAlign: 'center',
    fontSize: '11px',
    lineHeight: '15px',
    borderRadius: '3px',
    color: '#fff',
    display: 'inline-block',
    padding: '1px 6px',
    marginBottom: '5px',
    fontFamily: 'Circular-Bold',
  },
};

export default withMobileDialog()(
  reduxForm({
    form: 'pad_details', // a unique identifier for this form
    validate,
  })(
    class PADSettingsForm extends Component {
      constructor(props) {
        super(props);
        this.state = {
          showPartialAmount: false,
          showHowItWorks: false,
          showExplainationType: 'optionA',
        };
      }

      setShowPartialAmount = status => {
        this.setState({ showPartialAmount: status });
      };

      setShowHowItWorks = (status, type) => {
        this.setState({
          showHowItWorks: status,
          showExplainationType: type,
        });
      };

      handleNextStepWithValidation = () => {
        if (this.props.valid) {
          this.props.handleNextPadStep();
        }
      };

      handleClosePADForm = () => {
        this.props.resetAutopayStep();
        this.props.togglePayment();
        this.setShowPartialAmount(false);
      };

      render() {
        const { handleSubmit, change } = this.props;
        const { showPartialAmount, showHowItWorks, showExplainationType } = this.state;
        const {
          has_active_pad,
          external_autopay_is_setup,
          external_autopay_status,
          external_autopay_min_day,
          tenant_province,
          enable_allow_pap_details_delete_by_residents,
          pad_detail_type,
          autopay_fee,
          pad_details,
        } = this.props.tenantOverview;

        let payingTotalAmount, partialMonthlyAmount, isPartialAmount;

        const currentPadDetails = this.props.currentPadDetailForm.pad_details;
        const autopayOff = !(has_active_pad || external_autopay_is_setup);
        const isSplitPay =
          !featureEnabled('external_autopay') && featureEnabled('splitpay') && !hostIsCapreit();
        const showAutopayActiveDetails =
          this.props.autopayStep === AUTOPAY_INFORMATION_STEP && has_active_pad;
        let autoPayTitleVerbiage = getTranslatedString('autopay.setUp');
        let showAutopayAmountDetails =
          featureEnabled('external_autopay') &&
          this.props.autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP;

        if (currentPadDetails?.values) {
          payingTotalAmount = currentPadDetails.values.partial_or_total_amount;
          partialMonthlyAmount = currentPadDetails.values.partial_monthly_amount;
          isPartialAmount = payingTotalAmount;
        }

        //function that will store value for auto pay monthly authorized amount
        let autoPayMonthlyValue = () => {
          return (
            <div>
              {this.props.padDetails?.pay_total_amount &&
                getTranslatedString('autopay.monthlyBalanceValuePlaceHolder')}
              {!this.props.padDetails?.pay_total_amount &&
                formatter.formatMoney(this.props.padDetails?.authorized_amount)
              }
            </div>
          );
        };

        if (
          featureEnabled('external_autopay') &&
          this.props.autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP
        ) {
          autoPayTitleVerbiage = getTranslatedString('autopay.autopayAmount');
        }

        if (isSplitPay) {
          if (this.props.autopayStep === AUTOPAY_MODIFY_MONTHLY_AMOUNT) {
            autoPayTitleVerbiage = getTranslatedString('autopay.paymentAmount');
          }

          if (this.props.autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP) {
            autoPayTitleVerbiage = getTranslatedString('autopay.paymentAmount');
            showAutopayAmountDetails = isSplitPay;
          }

          if (this.props.autopayStep === AUTOPAY_CONFIRMATION_STEP) {
            autoPayTitleVerbiage = getTranslatedString('autopay.doesThisLookRight');
          }
        }

        return (
          <div>
            {this.props.autopayStep !== AUTOPAY_SUBMIT_MONTHLY_AMOUNT && (
              <Dialog
                disableBackdropClick
                fullScreen={this.props.fullScreen}
                open={this.props.isOpen}
                onClose={this.handleClosePADForm}
                isModal
                aria-labelledby="add-payment-details"
                classes={{
                  paper: 'form-dialog',
                }}
              >
                <DialogTitle id="add-payment-details">
                  {!hostIsBoardwalk() && (
                    <div style={{ margin: '20px', textAlign: 'center' }}>
                      <img src={billIcon} alt="loader" />
                    </div>
                  )}
                  <h3 style={{ textAlign: 'center', margin: 0 }}>{autoPayTitleVerbiage}</h3>
                </DialogTitle>
                <DialogContent>
                  {this.props.autopayStep !== AUTOPAY_CONFIRMATION_STEP && (
                    <div>
                      <AutoPayMessage
                        externalAutopayMinDay={external_autopay_min_day}
                        lastAutoPaySetupDate={this.props.lastAutoPaySetupDate}
                        autopayStep={this.props.autopayStep}
                        hasActivePad={has_active_pad}
                      />
                      <AutoPayStatusBadge
                        hasActivePad={has_active_pad}
                        hasExternalAutopay={external_autopay_is_setup}
                        externalAutopayStatus={external_autopay_status}
                        externalAutopayLastWithdrawal={this.props.externalAutopayLastWithdrawal}
                        autopayOff={autopayOff}
                      />
                    </div>
                  )}
                  <form onSubmit={handleSubmit}>
                    <AutoPayExplaination
                      type={showExplainationType}
                      isOpen={showHowItWorks}
                      onClose={() => this.setShowHowItWorks(false)}
                    />
                    {this.props.autopayStep === AUTOPAY_CONFIRMATION_STEP && (
                      <AutoPayConfirmation
                        description={getTranslatedString('autopay.almostDoneConfirmation')}
                        showProcessFeeTip={true}
                        type={isPartialAmount ? 'partial' : 'full'}
                        specificAmount={isPartialAmount && partialMonthlyAmount}
                        processFee={formatter.moneyToFloat(autopay_fee)}
                        footer={getTranslatedString('autopay.variableCostNote')}
                      />
                    )}
                    {this.props.autopayStep === AUTOPAY_DETAILS_STEP && (
                      <AutoPayDetails
                        externalAutopayMinDay={external_autopay_min_day}
                        padDetailType={pad_detail_type}
                        province={tenant_province}
                      />
                    )}
                    {(showAutopayAmountDetails ||
                      this.props.autopayStep === AUTOPAY_MODIFY_MONTHLY_AMOUNT) && (
                      <AutoPayAmountDetails
                        change={change}
                        padDetails={pad_details}
                        showPartialAmount={showPartialAmount}
                        setShowPartialAmount={this.setShowPartialAmount}
                        setShowHowItWorks={this.setShowHowItWorks}
                        showHowItWorks={showHowItWorks}
                      />
                    )}
                  </form>

                  {showAutopayActiveDetails && (
                    <div className="row" style={{ margin: '5px 0 15px 0' }}>
                      <AutoPayActiveDetails
                        isSplitPay={isSplitPay}
                        modifyPadDetails={this.handleNextStepWithValidation}
                        modifyPaymentAmount={() => this.props.handleNextPadStep('modify_monthly_amount')}
                        autoPayMonthlyValue={autoPayMonthlyValue}
                        processFee={formatter.moneyToFloat(autopay_fee)}
                        padDetails={this.props.padDetails}
                      />
                    </div>
                  )}
                </DialogContent>
                <DialogActions
              style={{ display: 'flex', justifyContent: 'space-between', margin: '15px' }}
            >
              <PADSettingsFormBackCloseCTA
                handleClosePADForm={this.handleClosePADForm}
                autopayStep={this.props.autopayStep}
                handlePADSettingFormBackClose={this.props.handleBackAutopayStep}
              />
              <PADSettingsFormCTA
                enableDeleteByResident={enable_allow_pap_details_delete_by_residents}
                onDeletePAP={this.props.onDeletePAP}
                hasActivePad={has_active_pad}
                lastAutoPaySetupDate={this.props.lastAutoPaySetupDate}
                handleNextStepWithValidation={this.handleNextStepWithValidation}
                handleSubmit={handleSubmit}
                handleNextPadStep={this.props.handleNextPadStep}
                autopayStep={this.props.autopayStep}
              />
            </DialogActions>
            </Dialog>
            )}
          </div>
        );
      }
    }
  )
);

const PADSettingsFormBackCloseCTA = ({
  handleClosePADForm,
  autopayStep,
  handlePADSettingFormBackClose,
}) => {
  let label = getTranslatedString('close');
  const isSplitPay =
    !featureEnabled('external_autopay') && featureEnabled('splitpay') && !hostIsCapreit();
  let onClick = () => {
    handleClosePADForm();
  };

  if (
    (featureEnabled('external_autopay') && autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP) ||
    autopayStep === AUTOPAY_DETAILS_STEP ||
    autopayStep === AUTOPAY_CONFIRMATION_STEP
  ) {
    label = getTranslatedString('autopay.back');
    onClick = () => {
      handlePADSettingFormBackClose();
    };
  }

  if (
    (isSplitPay && autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP) ||
    autopayStep === AUTOPAY_DETAILS_STEP ||
    autopayStep === AUTOPAY_CONFIRMATION_STEP ||
    autopayStep === AUTOPAY_MODIFY_MONTHLY_AMOUNT
  ) {
    label = getTranslatedString('autopay.back');
    onClick = () => {
      handlePADSettingFormBackClose();
    };
  }
  
  return (
    <FlatButton
      label={label}
      onClick={onClick}
      id="close-button"
      style={{ color: AccentColor() }}
    />
  );
};

const PADSettingsFormCTA = ({
  hasActivePad,
  handleNextStepWithValidation,
  handleSubmit,
  handleNextPadStep,
  autopayStep,
  onDeletePAP,
  enableDeleteByResident
}) => {
  let label = getTranslatedString('getStarted');
  const isSplitPay =
    !featureEnabled('external_autopay') && featureEnabled('splitpay') && !hostIsCapreit();

  if (featureEnabled('external_autopay') && autopayStep !== AUTOPAY_INFORMATION_STEP) {
    label = getTranslatedString('autopay.continue');
  }

  if ((isSplitPay || hasActivePad) && autopayStep !== AUTOPAY_INFORMATION_STEP) {
    label = getTranslatedString('autopay.continue');
  }

  if (autopayStep === AUTOPAY_INFORMATION_STEP && hasActivePad) {
    if(enableDeleteByResident) {
      label = getTranslatedString('autopay.deletePAPDetails');
    } else {
      label = getTranslatedString('autopay.edit');
    }
  }

  if (autopayStep === AUTOPAY_CONFIRMATION_STEP) {
    label = getTranslatedString('autopay.looksGoodtoMe');
  }

  const handleClick = () => {

    if (autopayStep === AUTOPAY_INFORMATION_STEP && hasActivePad && enableDeleteByResident) {
      onDeletePAP();
      return;
    }

    if (autopayStep === AUTOPAY_INFORMATION_STEP) {
      handleNextPadStep();
      return;
    }

    handleNextStepWithValidation();

    // Check if external autopay active and is not going to show the AUTOPAY_PAYMENT_ASSIGNMENT_STEP next.
    if (
      !featureEnabled('external_autopay') ||
      (autopayStep === AUTOPAY_DETAILS_STEP && !hostIsCapreit()) ||
      autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP
    ) {
      handleSubmit();
    }
  };

  return (
    <FlatButton
      label={label}
      onClick={handleClick}
      primary={true}
      backgroundColor={AccentColor()}
      hoverColor="grey"
      style={{ color: 'white' }}
    />
  );
};

const AutoPayMessage = ({
  externalAutopayMinDay,
  lastAutoPaySetupDate,
  autopayStep,
  hasActivePad,
}) => {
  let autopayMinDate = moment(new Date().setDate(`${externalAutopayMinDay}`))
    .locale(getCurrentLocale())
    .format('LL');

  let autoPayMessage = (
    <div>
      <p style={{ marginBottom: '20px' }}>
        {!hasActivePad && getTranslatedString('autopay.setUpPreAuthorizedMonthly')}
        {hasActivePad && getTranslatedString('autopay.editPreAuthorizedMonthlyDescription')}
      </p>
      <h5 style={{ fontSize: '16px' }}>
        {!hasActivePad &&
          getTranslatedString('autopay.setUpTimeRequired', {
            autopayMinDate: autopayMinDate,
          })}
      </h5>
    </div>
  );

  if (hostIsBoardwalk()) {
    return (
      <div className="row" style={{ margin: '5px 0 5px 0' }}>
        <p style={{ margin: 0, textAlign: 'justify' }}>
          {' '}
          {getTranslatedString('payment.bambora.autopayDetails', {
            autopayDay: externalAutopayMinDay,
          })}{' '}
          <i className="em em-money_with_wings" />
        </p>
      </div>
    );
  }

  if (featureEnabled('external_autopay')) {
    if (lastAutoPaySetupDate && autopayStep === AUTOPAY_DETAILS_STEP) {
      return (
        <div style={{ fontSize: '16px', textAlign: 'center', padding: '0px 30px' }}>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '20px' }}>
              {getTranslatedString('autopay.setUpTimeRequired', {
                autopayMinDate: autopayMinDate,
              })}
            </p>
            <h5 style={{ fontSize: '16px' }}>{getTranslatedString('autopay.doYouWantToCancel')}</h5>
          </div>
        </div>
      );
    }
    if (lastAutoPaySetupDate && autopayStep === AUTOPAY_INFORMATION_STEP) {
      return (
        <div style={{ fontSize: '16px', textAlign: 'center', padding: '0px 30px' }}>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '20px' }}>
              {getTranslatedString('autopay.modifyPreAuthorizedMonthly')}
            </p>
            <h5 style={{ fontSize: '16px' }}>
              {getTranslatedString('autopay.setUpTimeRequired', {
                autopayMinDate: autopayMinDate,
              })}
            </h5>
          </div>
        </div>
      );
    }

    if (autopayStep === AUTOPAY_INFORMATION_STEP) {
      return (
        <div style={{ fontSize: '16px', textAlign: 'center', padding: '0px 30px' }}>
          <p style={{ marginBottom: '20px' }}>
            {!hasActivePad && getTranslatedString('autopay.setUpPreAuthorizedMonthly')}
            {hasActivePad && getTranslatedString('autopay.editPreAuthorizedMonthlyDescription')}
          </p>
          <h5 style={{ fontSize: '16px' }}>
            {getTranslatedString('autopay.pleaseNote', {
              autopayMinDate: autopayMinDate,
            })}
          </h5>
        </div>
      );
    }

    if (autopayStep === AUTOPAY_DETAILS_STEP) {
      return (
        <div style={{ fontSize: '16px', textAlign: 'center', padding: '0px 30px' }}>
          <p style={{ marginBottom: '45px' }}>
            {getTranslatedString('autopay.setUpPreAuthorizedDetails', {
              autopayMinDate: autopayMinDate,
            })}
          </p>
        </div>
      );
    }

    if (autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP) {
      autoPayMessage = (
        <p style={{ marginBottom: '20px' }}>
          {getTranslatedString('autopay.withAutopayYouCanChooseToPay')}
        </p>
      );
    }

    return (
      <div style={{ fontSize: '16px', textAlign: 'center', padding: '0px 30px' }}>
        {autoPayMessage}
      </div>
    );
  } else {
    if (
      autopayStep === AUTOPAY_MODIFY_MONTHLY_AMOUNT ||
      (autopayStep === AUTOPAY_PAYMENT_ASSIGNMENT_STEP &&
        featureEnabled('splitpay') &&
        !hostIsCapreit())
    ) {
      autoPayMessage = (
        <div>
          <p style={{ marginBottom: '0px' }}>
            {getTranslatedString('autopay.monthlyPaymentPreferenceDetails')}
          </p>
          <p>{getTranslatedString('autopay.selectYourPreferenceBelow')}</p>
        </div>
      );
    }

    if (
      autopayStep === AUTOPAY_INFORMATION_STEP &&
      featureEnabled('splitpay') &&
      !hostIsCapreit()
    ) {
      autoPayMessage = (
        <p style={{ marginBottom: '20px' }}>
          {!hasActivePad && getTranslatedString('autopay.splitpay.getStarted')}
          {hasActivePad && getTranslatedString('autopay.editPreAuthorizedMonthlyDescription')}
        </p>
      );
    }

    if (autopayStep === AUTOPAY_DETAILS_STEP && featureEnabled('splitpay') && !hostIsCapreit()) {
      autoPayMessage = (
        <p style={{ marginBottom: '20px' }}>
          {getTranslatedString('autopay.splitpay.description')}
        </p>
      );
    }

    return (
      <div style={{ fontSize: '16px', textAlign: 'center', padding: '0px 30px' }}>
        {autoPayMessage}
      </div>
    );
  }
};

const AutoPayStatusBadge = ({
  hasActivePad,
  hasExternalAutopay,
  externalAutopayStatus,
  externalAutopayLastWithdrawal,
  autopayOff,
}) => {
  if (featureEnabled('external_autopay') && !hostIsBoardwalk()) return null;

  const autopayActive =
    hasActivePad || (hasExternalAutopay && externalAutopayStatus === 'approved');
  const autopayPending = hasExternalAutopay && externalAutopayStatus === 'pending';
  const autopayCancelled = autopayOff && externalAutopayStatus === 'cancelled';

  return (
    <div className="row" style={{ textAlign: 'center' }}>
      {autopayActive && (
        <span className="autopay-active" style={style.badge}>
          {getTranslatedString('dashboard.autopayIsOn')}
        </span>
      )}
      {autopayPending && (
        <span className="autopay-active" style={style.badge}>
          {getTranslatedString('dashboard.autopayIsPending')}
        </span>
      )}
      {autopayCancelled && (
        <Fragment>
          <span className="autopay-cancelled" style={style.badge}>
            {getTranslatedString('dashboard.autopayWasCancelled')}
          </span>
          {externalAutopayLastWithdrawal && (
            <p>
              {getTranslatedString('tenantPortal.autopay.lastWithdrawalDate', {
                date: formatter.toFriendlyDateString(externalAutopayLastWithdrawal, {
                  language: getCurrentLocale(),
                }),
              })}
            </p>
          )}
        </Fragment>
      )}
      {autopayOff &&
        !autopayCancelled && (
          <span className="pending" style={style.badge}>
            {getTranslatedString('dashboard.autopayIsOff')}
          </span>
        )}
    </div>
  );
};
